import BaseCrudService from './BaseCrudService';

/**
 * Global search.
 *
 * Every call is relayed by /api/search, which applies the caller's permission
 * filter server-side before forwarding to Meilisearch. The search engine itself
 * is bound to loopback and is not reachable from the browser.
 *
 * Responses are shaped:
 *   { query, total, processingTimeMs, facets: { type: count }, results: [...] }
 *
 * Result titles and descriptions may contain U+0001/U+0002 highlight markers —
 * render them with the Highlighted helper in components/SearchBar.js rather
 * than injecting them as HTML.
 */
class SearchService extends BaseCrudService {
  constructor() {
    super('/api/search');
  }

  /**
   * Full search across every indexed type.
   * @param {string} query
   * @param {{ type?: string, limit?: number, offset?: number }} [options]
   */
  searchAll(query, { type = null, limit = 20, offset = 0 } = {}) {
    return this.customRequest('get', '', null, {
      params: { q: query, type: type || undefined, limit, offset }
    });
  }

  /**
   * Short result set for the type-ahead dropdown.
   * @param {string} query
   * @param {{ type?: string }} [options]
   */
  getSuggestions(query, { type = null } = {}) {
    return this.customRequest('get', 'suggestions', null, {
      params: { q: query, type: type || undefined }
    });
  }

  /** Index health, document count and top zero-result queries. Needs search:admin. */
  getStatus() {
    return this.customRequest('get', 'status');
  }

  /** Menu links that can be indexed, with their on/off state. Needs search:admin. */
  getSources() {
    return this.customRequest('get', 'sources');
  }

  /** Replace the set of result types excluded from search. Needs search:admin. */
  setDisabledTypes(disabled) {
    return this.customRequest('put', 'sources', { disabled });
  }

  /** Rebuild the index. Needs search:admin. */
  reindex({ only = null, purge = false } = {}) {
    return this.customRequest('post', 'reindex', { only, purge });
  }
}

export default new SearchService();
